// PeptideVault 9.0.0 — reviewed supplement-label arithmetic.
// Extraction may be assisted by AI; normalization and totals are deterministic.

const NAME_ALIASES = new Map([
  ['vitamin b 12','vitamin b12'], ['b12','vitamin b12'],
  ['vitamin d 3','vitamin d3'], ['d3','vitamin d3'],
  ['magnesium glycinate','magnesium (glycinate)'],
  ['l arginine','l-arginine'], ['l citrulline','l-citrulline'],
  ['n acetyl cysteine','n-acetyl cysteine'], ['nac','n-acetyl cysteine']
])

function canonicalIngredientName(value) {
  const normalized = String(value || '').trim().toLowerCase()
    .replace(/[®™]/g,'').replace(/[–—]/g,'-').replace(/\s+/g,' ')
  return NAME_ALIASES.get(normalized) || normalized
}

function labelUnit(value) {
  const unit = String(value || '').trim().toLowerCase().replace(/μ/g,'u')
  if (unit === 'ug') return 'mcg'
  if (unit === 'i.u.' || unit === 'i.u') return 'IU'
  return unit === 'iu' ? 'IU' : unit
}

function convertLabelAmount(amount, fromUnit, toUnit) {
  const value = Number(amount), from = labelUnit(fromUnit), to = labelUnit(toUnit)
  if (!Number.isFinite(value) || !from || !to) return null
  if (from === to) return value
  const mg = { g:1000, mg:1, mcg:0.001 }
  if (mg[from] && mg[to]) return value * mg[from] / mg[to]
  return null
}

function normalizeIngredient(raw, index) {
  const name = String(raw && raw.name || '').trim()
  const amount = raw && raw.amount !== '' && raw.amount != null && Number.isFinite(Number(raw.amount)) ? Number(raw.amount) : null
  const quantityKnown = raw && raw.quantityKnown === false ? false : amount !== null
  return {
    id:String(raw && raw.id || `ingredient_${index + 1}`),
    name,
    canonicalName:canonicalIngredientName(raw && raw.canonicalName || name),
    amount:quantityKnown ? amount : null,
    unit:labelUnit(raw && raw.unit),
    form:String(raw && raw.form || '').trim(),
    blendName:String(raw && raw.blendName || '').trim(),
    isBlendTotal:!!(raw && raw.isBlendTotal),
    isBlendComponent:!!(raw && raw.isBlendComponent),
    quantityKnown,
    otherIngredient:!!(raw && raw.otherIngredient),
    confidence:raw && Number.isFinite(Number(raw.confidence)) ? Number(raw.confidence) : null,
    note:String(raw && raw.note || '').trim()
  }
}

function normalizeLabelFacts(raw) {
  const serving = raw && raw.serving || {}
  const servingUnits = Number(serving.units)
  return {
    serving:{
      units:Number.isFinite(servingUnits) && servingUnits > 0 ? servingUnits : null,
      form:String(serving.form || '').trim(),
      label:String(serving.label || '').trim()
    },
    ingredients:(raw && Array.isArray(raw.ingredients) ? raw.ingredients : []).map(normalizeIngredient).filter(item => item.name),
    directions:String(raw && raw.directions || '').trim(),
    notes:String(raw && raw.notes || '').trim()
  }
}

function ingredientPerUnit(ingredient, serving) {
  if (!ingredient || !ingredient.quantityKnown || !Number.isFinite(Number(ingredient.amount))) return { amount:null, unit:ingredient && ingredient.unit || '', known:false }
  const units = Number(serving && serving.units)
  if (!(units > 0)) return { amount:null, unit:ingredient.unit || '', known:false }
  return { amount:Number(ingredient.amount) / units, unit:ingredient.unit || '', known:true }
}

function reviewedLabelForSupplement(reviews, supplementId) {
  return (reviews || []).filter(review => review.supplementId === supplementId && review.status === 'reviewed')
    .sort((a,b) => String(b.reviewedAt || b.updatedAt || '').localeCompare(String(a.reviewedAt || a.updatedAt || '')))[0] || null
}

function ingredientKey(ingredient) {
  return `${canonicalIngredientName(ingredient && (ingredient.canonicalName || ingredient.name))}::${labelUnit(ingredient && ingredient.unit)}`
}

function formulaDiff(previousFacts, nextFacts) {
  const before = new Map((previousFacts && previousFacts.ingredients || []).map(item => [ingredientKey(item), item]))
  const after = new Map((nextFacts && nextFacts.ingredients || []).map(item => [ingredientKey(item), item]))
  const added = [], removed = [], changed = []
  after.forEach((item,key) => {
    if (!before.has(key)) added.push(item)
    else {
      const old = before.get(key)
      if (old.amount !== item.amount || old.quantityKnown !== item.quantityKnown || old.form !== item.form || old.blendName !== item.blendName) changed.push({ before:old, after:item })
    }
  })
  before.forEach((item,key) => { if (!after.has(key)) removed.push(item) })
  return { added, removed, changed, hasChanges:!!(added.length || removed.length || changed.length) }
}

function dailyIngredientTotals(supplements, reviews, person, date, servingResolver, eligibilityResolver) {
  const totals = new Map()
  ;(supplements || []).forEach(supplement => {
    if (eligibilityResolver && !eligibilityResolver(supplement, person, date)) return
    const review = reviewedLabelForSupplement(reviews, supplement.id)
    if (!review || !review.facts) return
    const slots = ['am','noon','pm','protocol']
    slots.forEach(slot => {
      const scheduledUnits = Math.max(0, Number(servingResolver(supplement, person, slot, date)) || 0)
      if (!(scheduledUnits > 0)) return
      ;(review.facts.ingredients || []).filter(item => !item.otherIngredient && !item.isBlendTotal).forEach(ingredient => {
        const key = ingredientKey(ingredient)
        if (!totals.has(key)) totals.set(key, { name:ingredient.name, canonicalName:ingredient.canonicalName, unit:ingredient.unit, amount:0, incomplete:false, contributions:[] })
        const total = totals.get(key), perUnit = ingredientPerUnit(ingredient, review.facts.serving)
        const contribution = perUnit.known ? perUnit.amount * scheduledUnits : null
        if (contribution == null) total.incomplete = true
        else total.amount += contribution
        total.contributions.push({ supplementId:supplement.id, supplementName:supplement.name || 'Supplement', slot, scheduledUnits, perUnit:perUnit.amount, amount:contribution })
      })
    })
  })
  return [...totals.values()].sort((a,b) => a.name.localeCompare(b.name))
}

function parseLabelExtraction(text) {
  const cleaned = String(text || '').replace(/```json|```/gi,'').trim()
  const parsed = JSON.parse(cleaned)
  return normalizeLabelFacts(parsed)
}

export {
  canonicalIngredientName, labelUnit, convertLabelAmount, normalizeIngredient,
  normalizeLabelFacts, ingredientPerUnit, reviewedLabelForSupplement,
  formulaDiff, dailyIngredientTotals, parseLabelExtraction
}
